import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { addMinutes, endOfDay, formatISO, startOfDay } from 'date-fns';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EventAttendeeTypeEnum } from '../Interfaces/Calendar/EventAttendeeTypeEnum';
import { EventBodyContentTypeEnum } from '../Interfaces/Calendar/EventBodyContentTypeEnum';
import { ICalendarEvent } from '../Interfaces/Calendar/ICalendarEvent';
import {
  calendarEventFormSchema,
  ICalendarEventForm,
} from '../Interfaces/Calendar/ICalendarEventForm';
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DATE_TIME_VIEW,
  DATE_VIEW,
  Messages,
} from '../constants';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingDialog } from '../core/LoadingDialog';
import { CreatableSearchableMultiSelectAutocomplete } from '../core/inputs/CreatableSearchableMultiSelectAutocomplete';
import { DateTimePickerInput } from '../core/inputs/DateTimePickerInput';
import { SwitchInput } from '../core/inputs/SwitchInput';
import { TextFieldInput } from '../core/inputs/TextFieldInput';
import {
  createOption,
  issueAttendees,
  issueOptionLabel,
  isValidEmail,
  transformEvent,
} from './utilities';
import {
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
} from '../hooks/calendarHooks';
import { useCalendarConnections } from '../hooks/reactQuerys/CalendarConnectionQueries';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';
import { useGetLegalTransactions } from '../hooks/reactQuerys/LegalTransactionQueries';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { useGetLocations } from '../hooks/reactQuerys/LocationQueries';
import { useGetRooms } from '../hooks/reactQuerys/RoomQueries';
import { ILocation } from '../Interfaces/ILocation';
import { IRoom } from '../Interfaces/IRoom';
import { RoomOccupancyPlan } from '../RealEstate/RoomOccupancyPlan';
import { useState } from 'react';

interface EventFormProps {
  userId: number;
  defaultValues: ICalendarEventForm;
  unmount: (editedEvent?: ICalendarEventForm, isDeleted?: boolean) => void;
}

export const EventFormDialog: React.FC<EventFormProps> = ({
  userId,
  defaultValues,
  unmount,
}) => {
  const { showAlert } = useAlert();
  const theme = useTheme();

  const formMethods = useForm<ICalendarEventForm>({
    defaultValues,
    resolver: zodResolver(calendarEventFormSchema),
    mode: 'onChange',
  });

  // Verbundene Kalender laden — bei mehr als einer aktiven Connection bietet
  // der Dialog ein Dropdown an. Ohne Connections fällt das Backend automatisch
  // auf die alte 1:1-O365-Logik zurück.
  const { connections } = useCalendarConnections(userId);
  const activeConnections = useMemo(
    () => connections.filter(c => c.IsActive),
    [connections]
  );
  const initialConnectionId =
    activeConnections.find(c => c.IsDefault)?.idUserCalendarConnection ??
    activeConnections[0]?.idUserCalendarConnection ??
    null;
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    number | null
  >(initialConnectionId);
  // Wenn Connections später nachgeladen werden (Race Condition), Default
  // einmalig setzen.
  useEffect(() => {
    if (selectedConnectionId === null && initialConnectionId !== null) {
      setSelectedConnectionId(initialConnectionId);
    }
  }, [initialConnectionId]);

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = formMethods;

  const isEdit = useMemo(() => !!defaultValues.id, [defaultValues]);

  // Vorgangs-Verknuepfung (Phase 4): optional einen Termin im zugehoerigen
  // Vorgang anlegen, damit Erinnerungen/Benachrichtigungen der Vorgangsart greifen.
  const { legalTransactions } = useGetLegalTransactions();
  const [linkToLegalTransaction, setLinkToLegalTransaction] = useState(false);
  const [selectedLegalTransaction, setSelectedLegalTransaction] =
    useState<ILegalTransaction | null>(null);
  const legalTransactionLabel = (lt: ILegalTransaction) =>
    (lt.Title && lt.Title.trim()) ||
    [lt.FirstName, lt.LastName].filter(Boolean).join(' ').trim() ||
    `Vorgang #${lt.idLegalTransaction}`;

  // Ort/Raum (Phase 4): hinterlegte Standorte auswaehlen (oder per Freitext),
  // bei Standorten mit Raeumen zusaetzlich einen Raum + Belegungsplan.
  const { locations = [] } = useGetLocations();
  const { rooms } = useGetRooms();
  const [locationValue, setLocationValue] = useState<ILocation | string | null>(
    defaultValues.location?.displayName || null
  );
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [occupancyOpen, setOccupancyOpen] = useState(false);

  const locationLabel = (o: ILocation | string) =>
    typeof o === 'string'
      ? o
      : o.Location || [o.Street, o.StreetNr].filter(Boolean).join(' ');

  const roomsOfLocation =
    typeof locationValue === 'object' && locationValue
      ? rooms.filter(r => r.Location_idLocation === locationValue.idLocation)
      : [];

  // Den Anzeigenamen (location.displayName) aus Standort (+ Raum) zusammensetzen
  // und in das Formular schreiben.
  const syncLocationDisplay = (
    loc: ILocation | string | null,
    roomId: number | null
  ) => {
    const locName = !loc
      ? ''
      : typeof loc === 'string'
        ? loc
        : locationLabel(loc);
    const room = roomId ? rooms.find(r => r.idRoom === roomId) : null;
    const display = [locName, room?.Name].filter(Boolean).join(' – ');
    setValue('location.displayName', display, { shouldDirty: true });
  };

  // Beim Anlegen den Standardstandort vorbelegen (sofern noch kein Ort gesetzt).
  useEffect(() => {
    if (defaultValues.id || locationValue) return;
    const def = locations.find(l => l.isDefault);
    if (def) {
      setLocationValue(def);
      const display =
        def.Location || [def.Street, def.StreetNr].filter(Boolean).join(' ');
      setValue('location.displayName', display, { shouldDirty: true });
    }
  }, [locations, defaultValues.id, locationValue, setValue]);

  const isDatePickerEndError = useMemo(
    () => !!errors.end,
    [errors.end, errors.start]
  );
  const isDatePickerStartError = useMemo(
    () => !!errors.start,
    [errors.end, errors.start]
  );
  const isAllDay = watch('isAllDay');
  const startDateTime = watch('start.dateTime');
  const endDateTime = watch('end.dateTime');

  // Set minDateTime for end.dateTime to prevent it from being before start.dateTime (when not all-day)
  const endMinDateTime = useMemo(
    () => (startDateTime ? new Date(startDateTime) : undefined),
    [startDateTime, isAllDay]
  );

  // Adjust start and end times when isAllDay changes
  useEffect(() => {
    if (isAllDay) {
      if (startDateTime) {
        const start = new Date(startDateTime);
        setValue(
          'start.dateTime',
          formatISO(startOfDay(start), { representation: 'complete' }),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      }
      if (endDateTime) {
        const end = new Date(endDateTime);
        setValue(
          'end.dateTime',
          formatISO(endOfDay(end), { representation: 'complete' }),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      }
    }
  }, [isAllDay, startDateTime, endDateTime, setValue]);

  // Reset end.dateTime if start.dateTime is set later than end.dateTime (when not all-day)
  useEffect(() => {
    if (!startDateTime || !endDateTime) return;

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    if (start > end) {
      const newEndDateTime = addMinutes(start, 30);
      setValue(
        'end.dateTime',
        formatISO(newEndDateTime, { representation: 'complete' }),
        { shouldValidate: true, shouldDirty: true }
      );
    }
  }, [startDateTime, endDateTime, setValue]);

  const { createCalendarEvent, isPendingCreateCalendarEvent } =
    useCreateCalendarEvent(userId);
  const { updateCalendarEvent, isPendingUpdateCalendarEvent } =
    useUpdateCalendarEvent(userId);
  const { deleteCalendarEvent, isPendingDeleteCalendarEvent } =
    useDeleteCalendarEvent(userId);
  // Alle Mandanten laden (Hook ist disabled, solange kein searchUri da ist —
  // also brauchen wir mindestens einen URI-Stub mit grosser rowsPerPage,
  // damit alle bekannten Mandanten als Vorschlag im Teilnehmer-Dropdown
  // auftauchen).
  const { legalClients, isLoadingLegalClients } = useGetLegalClients(
    'page=1&rowsPerPage=1000'
  );

  const allAttendees = useMemo(() => {
    if (!legalClients || isLoadingLegalClients) return [];
    return issueAttendees(legalClients.results);
  }, [legalClients, isLoadingLegalClients]);

  const onSubmit = useCallback(
    async (data: ICalendarEventForm) => {
      try {
        const requiredAttendees = data.requiredAttendees.map(a => ({
          emailAddress: {
            name: a.emailAddress.name,
            address: a.emailAddress.address,
          },
          type: EventAttendeeTypeEnum.Required,
        }));

        const optionalAttendees = data.optionalAttendees.map(a => ({
          emailAddress: {
            name: a.emailAddress.name,
            address: a.emailAddress.address,
          },
          type: EventAttendeeTypeEnum.Optional,
        }));

        const submitData: ICalendarEvent & {
          idUserCalendarConnection?: number | null;
          linkToLegalTransaction?: boolean;
          idLegalTransaction?: number | null;
          idLocation?: number | null;
          idRoom?: number | null;
        } = {
          ...data,
          body: { ...data.body, contentType: EventBodyContentTypeEnum.Text },
          attendees: [...requiredAttendees, ...optionalAttendees],
          idUserCalendarConnection: selectedConnectionId,
          linkToLegalTransaction:
            linkToLegalTransaction && !!selectedLegalTransaction,
          idLegalTransaction:
            selectedLegalTransaction?.idLegalTransaction ?? null,
          idLocation:
            typeof locationValue === 'object' && locationValue
              ? locationValue.idLocation
              : null,
          idRoom: selectedRoomId,
        };

        const updatedOrCreatedEvent: ICalendarEvent = submitData.id
          ? await updateCalendarEvent(submitData)
          : await createCalendarEvent(submitData);

        showAlert({
          text: Messages.ActionSuccessfully,
        });

        unmount(
          transformEvent(updatedOrCreatedEvent, legalClients?.results ?? [])
        );
      } catch (error) {
        console.error(error);
        showAlert({
          text: Messages.GeneralError,
          severity: 'error',
        });
      }
    },
    [
      createCalendarEvent,
      showAlert,
      unmount,
      updateCalendarEvent,
      selectedConnectionId,
      linkToLegalTransaction,
      selectedLegalTransaction,
      legalClients,
      locationValue,
      selectedRoomId,
    ]
  );

  const onDelete = useCallback(async () => {
    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie dieses Ereignis löschen wollen?',
      'Löschen',
      'Schließen',
      theme
    );
    if (isConfirm) {
      deleteCalendarEvent({
        ...defaultValues,
        idUserCalendarConnection: selectedConnectionId,
      });
      unmount(defaultValues, true);
    }
  }, [
    defaultValues,
    deleteCalendarEvent,
    selectedConnectionId,
    theme,
    unmount,
  ]);

  if (
    isPendingCreateCalendarEvent ||
    isPendingUpdateCalendarEvent ||
    isPendingDeleteCalendarEvent ||
    isLoadingLegalClients
  )
    return <LoadingDialog maxWidth='sm' />;

  return (
    <>
      {occupancyOpen && (
        <Dialog
          open
          onClose={() => setOccupancyOpen(false)}
          maxWidth='lg'
          fullWidth
        >
          <DialogTitle>Belegungsplan</DialogTitle>
          <DialogContent dividers>
            <RoomOccupancyPlan />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={() => setOccupancyOpen(false)}>
              Schließen
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog open={true} onClose={() => unmount()} maxWidth='sm' fullWidth>
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <Typography variant='h5'>
              {isEdit ? 'Ereignis bearbeiten' : 'Neues Ereignis erstellen'}
            </Typography>
            {isEdit && (
              <IconButton onClick={onDelete}>
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <FormProvider {...formMethods}>
          <Box component='form' noValidate onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              {activeConnections.length > 1 && (
                <TextField
                  select
                  fullWidth
                  size='small'
                  margin='dense'
                  label='Kalender'
                  value={selectedConnectionId ?? ''}
                  onChange={e =>
                    setSelectedConnectionId(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  {activeConnections.map(c => (
                    <MenuItem
                      key={c.idUserCalendarConnection}
                      value={c.idUserCalendarConnection}
                    >
                      {c.Label}
                      {c.Provider === 'webdav' ? ' · WebDAV' : ' · M365'}
                      {c.IsDefault ? ' (Standard)' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextFieldInput
                name='subject'
                label='Titel'
                required
                size='small'
                margin='dense'
                fullWidth
              />
              <CreatableSearchableMultiSelectAutocomplete
                name='requiredAttendees'
                label='Erforderliche Teilnehmer'
                hint='Geben Sie eine valide E-Mail-Adresse ein, um einen externen Teilnehmer hinzuzufügen'
                options={allAttendees}
                getOptionLabel={issueOptionLabel}
                isOptionDisabled={option => !option.emailAddress.address}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                createOption={input => createOption(input)}
                validateInput={input => isValidEmail(input)}
                textFieldProps={{
                  size: 'small',
                  margin: 'dense',
                  fullWidth: true,
                }}
              />
              <CreatableSearchableMultiSelectAutocomplete
                name='optionalAttendees'
                label='Optionale Teilnehmer'
                hint='Geben Sie eine valide E-Mail-Adresse ein, um einen externen Teilnehmer hinzuzufügen'
                options={allAttendees}
                getOptionLabel={issueOptionLabel}
                isOptionDisabled={option => !option.emailAddress.address}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                createOption={input => createOption(input)}
                validateInput={input => isValidEmail(input)}
                textFieldProps={{
                  size: 'small',
                  margin: 'dense',
                  fullWidth: true,
                }}
              />
              <Box display='flex' alignItems='center' gap={2}>
                <Box flex={1} sx={{ mb: isDatePickerEndError ? 3 : 0 }}>
                  <DateTimePickerInput
                    name='start.dateTime'
                    label='Startzeit'
                    views={isAllDay ? DATE_VIEW : DATE_TIME_VIEW}
                    format={isAllDay ? DATE_FORMAT : DATE_TIME_FORMAT}
                    textFieldProps={{
                      required: true,
                      size: 'small',
                      margin: 'dense',
                      fullWidth: true,
                    }}
                  />
                </Box>
                <Box flex={1} sx={{ mb: isDatePickerStartError ? 3 : 0 }}>
                  <DateTimePickerInput
                    name='end.dateTime'
                    label='Endzeit'
                    views={isAllDay ? DATE_VIEW : DATE_TIME_VIEW}
                    format={isAllDay ? DATE_FORMAT : DATE_TIME_FORMAT}
                    textFieldProps={{
                      required: true,
                      size: 'small',
                      margin: 'dense',
                      fullWidth: true,
                    }}
                    minDateTime={endMinDateTime} // Prevent end before start
                  />
                </Box>
                <Box
                  sx={{
                    mb: isDatePickerStartError || isDatePickerEndError ? 3 : 0,
                  }}
                >
                  <SwitchInput name='isAllDay' label='Ganztägig' size='small' />
                </Box>
              </Box>
              <Autocomplete
                freeSolo
                options={locations}
                value={locationValue}
                getOptionLabel={locationLabel}
                isOptionEqualToValue={(o, v) =>
                  typeof o !== 'string' &&
                  typeof v !== 'string' &&
                  o.idLocation === v.idLocation
                }
                onChange={(_, v) => {
                  setLocationValue(v);
                  setSelectedRoomId(null);
                  syncLocationDisplay(v, null);
                }}
                onInputChange={(_, input, reason) => {
                  if (reason === 'input') {
                    setLocationValue(input);
                    setSelectedRoomId(null);
                    syncLocationDisplay(input, null);
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Ort'
                    size='small'
                    margin='dense'
                    fullWidth
                    helperText='Standort auswählen oder eigenen Ort eintippen.'
                  />
                )}
              />
              {roomsOfLocation.length > 0 && (
                <Box display='flex' alignItems='center' gap={1}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={roomsOfLocation}
                    value={rooms.find(r => r.idRoom === selectedRoomId) ?? null}
                    getOptionLabel={(o: IRoom) =>
                      o.Capacity != null
                        ? `${o.Name} (${o.Capacity} Pers.)`
                        : o.Name
                    }
                    isOptionEqualToValue={(o, v) => o.idRoom === v.idRoom}
                    onChange={(_, v) => {
                      setSelectedRoomId(v?.idRoom ?? null);
                      syncLocationDisplay(locationValue, v?.idRoom ?? null);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Raum'
                        size='small'
                        margin='dense'
                        fullWidth
                      />
                    )}
                  />
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => setOccupancyOpen(true)}
                  >
                    Belegungsplan
                  </Button>
                </Box>
              )}
              <TextFieldInput
                name='body.content'
                label='Beschreibung'
                size='small'
                margin='dense'
                fullWidth
                multiline
                rows={4}
              />

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={linkToLegalTransaction}
                    onChange={e => setLinkToLegalTransaction(e.target.checked)}
                  />
                }
                label='Mit Vorgang verknüpfen'
              />
              {linkToLegalTransaction && (
                <Autocomplete
                  options={legalTransactions ?? []}
                  value={selectedLegalTransaction}
                  getOptionLabel={legalTransactionLabel}
                  isOptionEqualToValue={(o, v) =>
                    o.idLegalTransaction === v.idLegalTransaction
                  }
                  onChange={(_, v) => setSelectedLegalTransaction(v)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Vorgang'
                      size='small'
                      margin='dense'
                      fullWidth
                      helperText='Der Termin erscheint im Vorgang unter „Termine“; Erinnerungen der Vorgangsart greifen.'
                    />
                  )}
                />
              )}
            </DialogContent>
            <DialogActions
              sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
            >
              <Button variant='outlined' onClick={() => unmount()}>
                Abbrechen
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting || !isDirty || !isValid}
              >
                {isEdit ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogActions>
          </Box>
        </FormProvider>
      </Dialog>
    </>
  );
};
